import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faBell, faCheck, faTrash, faFilter, faSearch } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import SEO from '../SEO';
import { seoConfig } from '../../utils/seoData';

const NotificationsPage: React.FC = () => {
  const navigate = useNavigate();
  const { lang } = useParams<{ lang: string }>();
  const { t } = useTranslation();
  const currentLang = lang || 'tr';
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Helper function to add language prefix to paths
  const getLocalizedPath = (path: string) => {
    return `/${currentLang}${path}`;
  };

  // Mock notifications data
  const [notifications, setNotifications] = useState([
    {
      id: 1,
      title: 'Yeni Takipçi',
      message: 'Instagram hesabınızda 25 yeni takipçi kazandınız. Bu hafta toplam 150 yeni takipçi kazandınız.',
      time: '2 dakika önce',
      isRead: false,
      type: 'success',
      category: 'engagement'
    },
    {
      id: 2,
      title: 'Rapor Hazır',
      message: 'Haftalık analitik raporunuz hazırlandı. Performansınız geçen haftaya göre %15 artış gösterdi.',
      time: '1 saat önce',
      isRead: false,
      type: 'info',
      category: 'analytics'
    },
    {
      id: 3,
      title: 'AI Önerisi',
      message: 'En iyi paylaşım zamanınız: 14:00-16:00 arası. Bu saatlerde paylaştığınız içerikler %30 daha fazla etkileşim alıyor.',
      time: '3 saat önce',
      isRead: true,
      type: 'warning',
      category: 'ai'
    },
    {
      id: 4,
      title: 'Sistem Güncellemesi',
      message: 'Yeni özellikler eklendi. Detayları görüntüleyin: Gelişmiş hashtag analizi ve otomatik içerik önerileri.',
      time: '1 gün önce',
      isRead: true,
      type: 'info',
      category: 'system'
    },
    {
      id: 5,
      title: 'Hesap Bağlantısı',
      message: 'LinkedIn hesabınız başarıyla bağlandı. Artık LinkedIn analitiklerinizi de takip edebilirsiniz.',
      time: '2 gün önce',
      isRead: true,
      type: 'success',
      category: 'integration'
    },
    {
      id: 6,
      title: 'Etkileşim Artışı',
      message: 'Son paylaştığınız gönderi beklenenden %40 daha fazla beğeni aldı. Bu trendi sürdürmek için benzer içerikler paylaşın.',
      time: '3 gün önce',
      isRead: true,
      type: 'success',
      category: 'engagement'
    },
    {
      id: 7,
      title: 'Haftalık Özet',
      message: 'Bu hafta toplam 2.4M görüntülenme, 156K etkileşim ve 12.8K yeni takipçi kazandınız.',
      time: '1 hafta önce',
      isRead: true,
      type: 'info',
      category: 'analytics'
    },
    {
      id: 8,
      title: 'Güvenlik Uyarısı',
      message: 'Hesabınızda olağandışı aktivite tespit edildi. Lütfen şifrenizi değiştirin.',
      time: '1 hafta önce',
      isRead: true,
      type: 'warning',
      category: 'security'
    }
  ]);

  const filters = [
    { id: 'all', label: t('notifications.all'), count: notifications.length },
    { id: 'unread', label: t('notifications.unread'), count: notifications.filter(n => !n.isRead).length },
    { id: 'engagement', label: t('notifications.engagement'), count: notifications.filter(n => n.category === 'engagement').length },
    { id: 'analytics', label: t('notifications.analytics'), count: notifications.filter(n => n.category === 'analytics').length },
    { id: 'ai', label: t('notifications.aiRecommendations'), count: notifications.filter(n => n.category === 'ai').length },
    { id: 'system', label: t('notifications.system'), count: notifications.filter(n => n.category === 'system').length }
  ];

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'success':
        return 'bg-green-500/20 border-green-400/30 text-green-400';
      case 'warning':
        return 'bg-yellow-500/20 border-yellow-400/30 text-yellow-400';
      case 'error':
        return 'bg-red-500/20 border-red-400/30 text-red-400';
      default:
        return 'bg-blue-500/20 border-blue-400/30 text-blue-400';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'success':
        return '✅';
      case 'warning':
        return '⚠️';
      case 'error':
        return '❌';
      default:
        return 'ℹ️';
    }
  };

  const markAsRead = (id: number) => {
    setNotifications(notifications.map(n => 
      n.id === id ? { ...n, isRead: true } : n
    ));
  };

  const markAllAsRead = () => {
    setNotifications(notifications.map(n => ({ ...n, isRead: true })));
  };

  const deleteNotification = (id: number) => {
    setNotifications(notifications.filter(n => n.id !== id));
  };

  const filteredNotifications = notifications.filter(notification => {
    const matchesFilter = activeFilter === 'all' || 
      (activeFilter === 'unread' ? !notification.isRead : notification.category === activeFilter);
    const matchesSearch = notification.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      notification.message.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  return (
    <>
      <SEO {...seoConfig.notifications} />
      <div className="min-h-screen bg-gray-900 text-white">
      {/* Header */}
      <div className="backdrop-blur-xl bg-white/5 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 md:space-x-4">
              <button 
                onClick={() => navigate(getLocalizedPath('/social-media'))}
                className="flex items-center space-x-2 text-white/80 hover:text-white transition-colors"
              >
                <FontAwesomeIcon icon={faArrowLeft} className="text-lg" />
                <span className="hidden sm:inline">{t('notifications.backToDashboard')}</span>
              </button>
            </div>
            <h1 className="text-xl md:text-2xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
              {t('notifications.title')}
            </h1>
            <div></div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-8">
        {/* Stats and Actions */}
        <div className="mb-6 md:mb-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 mb-4 md:mb-6">
            <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-xl p-6">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-700 rounded-xl flex items-center justify-center">
                  <FontAwesomeIcon icon={faBell} className="text-white text-lg" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-white">{notifications.length}</h3>
                  <p className="text-white/60 text-sm">{t('notifications.totalNotifications')}</p>
                </div>
              </div>
            </div>
            
            <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-xl p-6">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-gradient-to-br from-yellow-500 to-yellow-700 rounded-xl flex items-center justify-center">
                  <span className="text-white text-lg">🔔</span>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-white">{notifications.filter(n => !n.isRead).length}</h3>
                  <p className="text-white/60 text-sm">{t('notifications.unread')}</p>
                </div>
              </div>
            </div>
            
            <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-xl p-6">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-700 rounded-xl flex items-center justify-center">
                  <FontAwesomeIcon icon={faCheck} className="text-white text-lg" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-white">{notifications.filter(n => n.isRead).length}</h3>
                  <p className="text-white/60 text-sm">{t('notifications.read')}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Search and Actions */}
          <div className="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0 md:space-x-4">
            <div className="relative flex-1 max-w-md">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FontAwesomeIcon icon={faSearch} className="text-white/40 text-sm" />
              </div>
              <input
                type="text"
                placeholder={t('notifications.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-300"
              />
            </div>
            
            <div className="flex items-center space-x-3">
              <button
                onClick={markAllAsRead}
                className="flex items-center space-x-2 px-3 md:px-4 py-2.5 md:py-3 bg-white/10 border border-white/20 rounded-xl text-white hover:bg-white/20 transition-colors text-sm md:text-base"
              >
                <FontAwesomeIcon icon={faCheck} className="text-sm" />
                <span className="hidden sm:inline">{t('notifications.markAllRead')}</span>
                <span className="sm:hidden">{t('notifications.markAllReadShort')}</span>
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 md:gap-8">
          {/* Filters */}
          <div className="lg:col-span-1 order-2 lg:order-1">
            <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-xl p-4 md:p-6">
              <h3 className="text-base md:text-lg font-semibold text-white mb-3 md:mb-4">{t('notifications.filters')}</h3>
              <div className="space-y-2">
                {filters.map((filter) => (
                  <button
                    key={filter.id}
                    onClick={() => setActiveFilter(filter.id)}
                    className={`w-full flex items-center justify-between px-3 md:px-4 py-2.5 md:py-3 rounded-xl text-left transition-all duration-300 text-sm md:text-base ${
                      activeFilter === filter.id
                        ? 'bg-gradient-to-r from-purple-500/20 to-purple-700/20 border border-purple-400/30 text-white'
                        : 'text-white/70 hover:text-white hover:bg-white/5'
                    }`}
                  >
                    <span className="font-medium">{filter.label}</span>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      activeFilter === filter.id 
                        ? 'bg-purple-500/30 text-purple-300' 
                        : 'bg-white/10 text-white/60'
                    }`}>
                      {filter.count}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Notifications List */}
          <div className="lg:col-span-3 order-1 lg:order-2">
            <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-xl p-4 md:p-6">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 md:mb-6 gap-2">
                <h3 className="text-base md:text-lg font-semibold text-white">
                  {activeFilter === 'all' ? t('notifications.allNotifications') : 
                   activeFilter === 'unread' ? t('notifications.unreadNotifications') :
                   filters.find(f => f.id === activeFilter)?.label + ' ' + t('notifications.notificationsCount')}
                </h3>
                <span className="text-white/60 text-xs md:text-sm">
                  {filteredNotifications.length} {t('notifications.notificationsCount')}
                </span>
              </div>

              <div className="space-y-4">
                {filteredNotifications.length === 0 ? (
                  <div className="text-center py-12">
                    <FontAwesomeIcon icon={faBell} className="text-white/30 text-4xl mb-4" />
                    <h4 className="text-white/60 text-lg mb-2">{t('notifications.noNotifications')}</h4>
                    <p className="text-white/40 text-sm">
                      {searchTerm ? t('notifications.noNotificationsDescription') : t('notifications.noNotificationsCategory')}
                    </p>
                  </div>
                ) : (
                  filteredNotifications.map((notification) => (
                    <div
                      key={notification.id}
                      className={`backdrop-blur-xl border border-white/10 rounded-xl p-4 md:p-6 transition-all duration-300 ${
                        !notification.isRead 
                          ? 'bg-blue-500/5 border-blue-400/20' 
                          : 'bg-white/5 hover:bg-white/10'
                      }`}
                    >
                      <div className="flex items-start space-x-3 md:space-x-4">
                        <div className={`w-8 h-8 md:w-10 md:h-10 rounded-xl flex items-center justify-center text-base md:text-lg flex-shrink-0 ${
                          !notification.isRead ? 'bg-blue-500/20' : 'bg-white/10'
                        }`}>
                          {getTypeIcon(notification.type)}
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2 gap-2">
                            <h4 className={`text-base md:text-lg font-semibold ${
                              !notification.isRead ? 'text-white' : 'text-white/90'
                            }`}>
                              {notification.title}
                            </h4>
                            <div className="flex items-center space-x-2 flex-shrink-0">
                              {!notification.isRead && (
                                <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                              )}
                              <span className={`px-2 py-1 text-xs rounded-full border ${getTypeColor(notification.type)}`}>
                                {notification.type}
                              </span>
                            </div>
                          </div>
                          
                          <p className="text-white/70 text-sm mb-3 leading-relaxed">
                            {notification.message}
                          </p>
                          
                          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
                            <p className="text-white/50 text-xs">{notification.time}</p>
                            <div className="flex items-center space-x-2">
                              {!notification.isRead && (
                                <button
                                  onClick={() => markAsRead(notification.id)}
                                  className="px-2 md:px-3 py-1 bg-blue-500/20 border border-blue-400/30 rounded-lg text-blue-400 hover:bg-blue-500/30 transition-colors text-xs"
                                >
                                  <span className="hidden sm:inline">{t('notifications.markAsRead')}</span>
                                  <span className="sm:hidden">{t('notifications.markAsReadShort')}</span>
                                </button>
                              )}
                              <button
                                onClick={() => deleteNotification(notification.id)}
                                className="p-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors"
                              >
                                <FontAwesomeIcon icon={faTrash} className="text-sm" />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>
    </>
  );
};

export default NotificationsPage;
